import { useEffect, useMemo, useRef, useState } from "react"
import { useFrame, type ThreeEvent } from "@react-three/fiber"
import { damp3, dampQ } from "maath/easing"
import * as THREE from "three"

import { Avatar } from "./Avatar"
import { DescentBeam } from "./DescentBeam"
import { TargetIndicator } from "./TargetIndicator"
import type { AvatarAnimKey } from "./MaterializedMesh"
import { reachOut } from "../../hooks/useLink"
import { useInspectStore } from "../../stores/useInspectStore"
import type { NetworkPeer } from "../../stores/useNetworkStore"

// 2026-05-05 — Para peers remotos derivamos walk/idle a partir del
// delta de posición frame a frame (no tenemos un flag explícito de
// "moviendo" en PlayerSnapshot). Si la velocidad supera el umbral,
// el peer se anima caminando; si no, idle. Run y Wave de peers se
// implementarían cuando el server las broadcaste como banderas.
const REMOTE_MOVING_THRESHOLD = 0.4

// 2026-05-05 — Distinción tap vs hold sobre el avatar remoto.
//   · Tap rápido (<350ms, sin moverse) → abre panel 2D de inspección
//     (PeerInfoPanel) con el Índice de Silicio + 6 pilares del peer.
//   · Hold (≥350ms) → emite LINK_REACH (solicitud de enlace).
//     Mantiene la mecánica de la "Llave Doble" original.
// El threshold se cancela si el dedo se mueve >10px (cuenta como drag de
// cámara o movimiento del avatar local) — entonces no se dispara nada.
const TAP_HOLD_MS = 350
const TAP_MOVE_PX = 10

const POSITION_SMOOTH = 0.18
const ROTATION_SMOOTH = 0.15

// Avatar de un peer remoto.
//
// - Damping framerate-independent sobre snapshots a 10Hz.
// - PointerDown emite LINK_REACH (Llave Doble): mantengo el cursor 3s
//   sostenido sobre el otro avatar mientras el otro me corresponde para sellar.
// - Si el peer está anchored, los click handlers se quitan: el raycaster
//   atraviesa al avatar y va al MovementPlane (puedes caminar a través).
// - Sprint 3: si el peer recibió SPAWN_EVENT recientemente, pintamos el
//   haz de descenso y el avatar emerge con fade-in. Una vez completada
//   la animación, el peer se vuelve interactuable normal.
export function RemoteAvatar({ peer }: { peer: NetworkPeer }) {
    const groupRef = useRef<THREE.Group>(null)
    const targetPos = useMemo(
        () => new THREE.Vector3(...peer.position),
        []
    )
    const targetQuat = useMemo(
        () =>
            new THREE.Quaternion(
                peer.rotation[0],
                peer.rotation[1],
                peer.rotation[2],
                peer.rotation[3]
            ),
        []
    )

    useEffect(() => {
        if (groupRef.current) {
            groupRef.current.position.set(...peer.position)
            groupRef.current.quaternion.set(
                peer.rotation[0],
                peer.rotation[1],
                peer.rotation[2],
                peer.rotation[3]
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    targetPos.set(peer.position[0], peer.position[1], peer.position[2])
    targetQuat.set(
        peer.rotation[0],
        peer.rotation[1],
        peer.rotation[2],
        peer.rotation[3]
    )

    // Sprint 3 — fade-in del avatar emergiendo del haz.
    const [materializeOpacity, setMaterializeOpacity] = useState(() =>
        peer.descentStartedAtClient ? 0 : 1
    )

    // Animation key para el peer — derivado de movimiento.
    const [animKey, setAnimKey] = useState<AvatarAnimKey>("idle")
    const lastPosRef = useRef<[number, number, number]>(peer.position)
    const speedRef = useRef(0)
    const lastDecisionRef = useRef<AvatarAnimKey>("idle")

    useFrame((_, dt) => {
        const g = groupRef.current
        if (!g) return
        damp3(g.position, targetPos, POSITION_SMOOTH, dt)
        dampQ(g.quaternion, targetQuat, ROTATION_SMOOTH, dt)

        // Sprint 3 — animación de fade-in mientras el haz cae y emerge.
        if (peer.descentStartedAtClient && peer.descentMs && peer.materializeMs) {
            const elapsed = performance.now() - peer.descentStartedAtClient
            const into = Math.max(0, elapsed - peer.descentMs)
            const t =
                peer.materializeMs > 0
                    ? Math.min(1, into / peer.materializeMs)
                    : 0
            const eased = 1 - Math.pow(1 - t, 3)
            if (Math.abs(materializeOpacity - eased) > 0.005) {
                setMaterializeOpacity(eased)
            }
        } else if (materializeOpacity < 1) {
            setMaterializeOpacity(1)
        }

        // Decidir animationKey por velocidad del damp interpolado.
        const px = g.position.x
        const pz = g.position.z
        const dx = px - lastPosRef.current[0]
        const dz = pz - lastPosRef.current[2]
        const instSpeed = dt > 0 ? Math.hypot(dx, dz) / dt : 0
        speedRef.current = speedRef.current * 0.7 + instSpeed * 0.3
        lastPosRef.current = [px, g.position.y, pz]

        const desired: AvatarAnimKey =
            speedRef.current > REMOTE_MOVING_THRESHOLD ? "walk" : "idle"
        if (desired !== lastDecisionRef.current) {
            lastDecisionRef.current = desired
            setAnimKey(desired)
        }
    })

    // Estado del gesto in-flight: si pointerDown disparó timer de hold,
    // y dónde arrancó. Si se cumple el timer = hold = reachOut. Si se
    // suelta antes = tap = abrir panel.
    const tapState = useRef<{
        startX: number
        startY: number
        startTs: number
        holdTimer: number | null
        holdFired: boolean
    } | null>(null)

    const clearHold = () => {
        if (tapState.current?.holdTimer != null) {
            window.clearTimeout(tapState.current.holdTimer)
        }
    }

    const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation()
        const ne = e.nativeEvent as PointerEvent
        const holdTimer = window.setTimeout(() => {
            // Hold cumplido → emitir reach.
            if (tapState.current) {
                tapState.current.holdFired = true
            }
            reachOut(peer.id)
        }, TAP_HOLD_MS)
        tapState.current = {
            startX: ne.clientX,
            startY: ne.clientY,
            startTs: performance.now(),
            holdTimer,
            holdFired: false,
        }
    }

    const handlePointerMove = (e: ThreeEvent<PointerEvent>) => {
        if (!tapState.current) return
        const ne = e.nativeEvent as PointerEvent
        const dx = ne.clientX - tapState.current.startX
        const dy = ne.clientY - tapState.current.startY
        if (dx * dx + dy * dy > TAP_MOVE_PX * TAP_MOVE_PX) {
            // Se considera drag → cancelar hold y limpiar.
            clearHold()
            tapState.current = null
        }
    }

    const handlePointerUp = (e: ThreeEvent<PointerEvent>) => {
        if (!tapState.current) return
        e.stopPropagation()
        const fired = tapState.current.holdFired
        clearHold()
        const elapsed = performance.now() - tapState.current.startTs
        tapState.current = null
        // Si el hold no había disparado aún, fue un tap rápido → panel.
        if (!fired && elapsed < TAP_HOLD_MS) {
            useInspectStore.getState().open(peer.id)
        }
    }

    const handlePointerCancel = () => {
        clearHold()
        tapState.current = null
    }

    // Cuando el peer está anchored o está descendiendo, no hitbox.
    // El raycaster cae al MovementPlane que está detrás → el cursor
    // pasa "a través" del avatar como si fuese intangible.
    const isDescending =
        peer.descentStartedAtClient != null &&
        peer.descentMs != null &&
        performance.now() - peer.descentStartedAtClient < peer.descentMs
    const reachable = !peer.isAnchored && !isDescending

    const showBeam =
        peer.model &&
        peer.polarity &&
        peer.descentStartedAtClient != null &&
        peer.descentMs != null &&
        peer.materializeMs != null &&
        performance.now() - peer.descentStartedAtClient <
            peer.descentMs + peer.materializeMs

    return (
        <>
            {showBeam && (
                <DescentBeam
                    pad={peer.position}
                    model={peer.model!}
                    polarity={peer.polarity!}
                    startedAt={peer.descentStartedAtClient!}
                    descentMs={peer.descentMs!}
                    materializeMs={peer.materializeMs!}
                />
            )}
            <group ref={groupRef}>
                {/* Hitbox invisible que cubre al avatar — fácil de capturar.
                    Solo se monta cuando el peer NO está anchored: si está
                    anchored, el raycaster cae al MovementPlane atrás y el
                    cursor pasa "a través" como si el avatar fuese intangible. */}
                {reachable && (
                    <mesh
                        position={[0, 1, 0]}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerCancel}
                        onPointerLeave={handlePointerCancel}
                    >
                        <sphereGeometry args={[0.95, 12, 12]} />
                        <meshBasicMaterial
                            transparent
                            opacity={0}
                            depthWrite={false}
                        />
                    </mesh>
                )}

                <Avatar
                    siliconIndex={peer.siliconIndex}
                    displayName={peer.displayName}
                    isAnchored={peer.isAnchored}
                    model={peer.model ?? null}
                    polarity={peer.polarity ?? null}
                    materializeOpacity={materializeOpacity}
                    animationKey={animKey}
                />
            </group>

            {peer.target && !peer.isAnchored && !isDescending && (
                <TargetIndicator target={peer.target} color="#7DEFFF" />
            )}
        </>
    )
}
